import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GoogleLoginButton } from "../components/GoogleLoginButton";
import { MascotScene } from "../components/Mascot";
import { TelegramLoginButton } from "../components/TelegramLoginButton";
import { useAuth } from "../lib/auth-context";
import { loginWithGoogle, loginWithTelegram } from "../api/session";

const BOT_USERNAME = import.meta.env.VITE_BOT_USERNAME ?? "NifgashHaifabot";

export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { setUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [showTelegram, setShowTelegram] = useState(false);

  const redirectTo = (location.state as { from?: string } | null)?.from ?? "/events";

  async function handleGoogle(credential: string) {
    setError(null);
    try {
      const user = await loginWithGoogle(credential);
      // Lift the user into App-level state BEFORE navigating, otherwise the
      // destination route guard still sees `anon` and bounces back to /login.
      setUser(user);
      navigate(user.full_name ? redirectTo : "/register", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function handleTelegram(payload: Record<string, unknown>) {
    setError(null);
    try {
      const user = await loginWithTelegram(payload);
      setUser(user);
      navigate(user.full_name ? redirectTo : "/register", { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 text-center">
      <MascotScene width={260} />
      <h1 className="text-[26px] font-extrabold text-ink-900 mt-6 tracking-tight">
        {t("login.title")}
      </h1>
      <p className="text-[14px] text-ink-500 mt-2 max-w-[340px] leading-relaxed">
        {t("login.subtitle")}
      </p>

      <div className="mt-8">
        <GoogleLoginButton onCredential={handleGoogle} />
      </div>

      {error && (
        <p className="mt-4 text-[13px] text-brand-red max-w-[320px]">
          {t("login.errorPrefix")} {error}
        </p>
      )}

      <div className="mt-8">
        {showTelegram ? (
          <div className="flex flex-col items-center gap-3">
            <TelegramLoginButton botUsername={BOT_USERNAME} onAuth={handleTelegram} requestAccess />
            <button
              type="button"
              onClick={() => setShowTelegram(false)}
              className="text-[12px] text-ink-400 hover:text-ink-700 underline underline-offset-4"
            >
              {t("login.hideTg")}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setShowTelegram(true)}
            className="text-[13px] text-ink-500 hover:text-ink-900 underline underline-offset-4"
          >
            {t("login.tgFallback")}
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate("/")}
        className="mt-10 text-[13px] text-ink-400 hover:text-ink-700 underline underline-offset-4"
      >
        {t("login.backHome")}
      </button>
    </div>
  );
}
